import { AdminShell } from "@project/admin/components/admin-shell";
import { StatCard, SectionCard } from "@project/admin/components/module-ui";
import { Badge } from "@project/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import { Bell, MessageSquare, Mail, Send } from "lucide-react";
import {
  mockNotifications,
  mockSmsTemplates,
  mockEmailTemplates,
} from "@project/lib/mock-modules2";

export default function CommunicationDashboard() {
  const totalSentToday = 24 + 1840 + 38; // illustrative aggregate

  return (
    <AdminShell
      title="Communication Center"
      subtitle="Notifications, templates & recent outreach"
    >
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Messages Sent Today"
          value={totalSentToday.toLocaleString("en-IN")}
          icon={<Send className="h-5 w-5" />}
          accent="from-primary/90 to-primary"
          sub="Across SMS, Email & Push"
        />
        <StatCard
          label="Notifications"
          value={mockNotifications.length}
          icon={<Bell className="h-5 w-5" />}
          accent="from-amber-600 to-amber-500"
          sub="Last 24 hours"
        />
        <StatCard
          label="SMS Templates"
          value={mockSmsTemplates.length}
          icon={<MessageSquare className="h-5 w-5" />}
          accent="from-emerald-600 to-emerald-500"
          sub="Active templates"
        />
        <StatCard
          label="Email Templates"
          value={mockEmailTemplates.length}
          icon={<Mail className="h-5 w-5" />}
          accent="from-rose-600 to-rose-500"
          sub="Active templates"
        />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <SectionCard
          title="Recent Communications"
          subtitle="Last messages dispatched"
          className="lg:col-span-2"
        >
          <ul className="space-y-3">
            {mockNotifications.map(n => (
              <li
                key={n.id}
                className="bg-background/40 flex items-start gap-3 rounded-lg border border-border p-3"
              >
                <Badge variant="secondary" className="shrink-0">
                  {n.channel}
                </Badge>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-foreground">{n.text}</div>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    {n.when}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </SectionCard>

        <SectionCard title="Notifications" subtitle="Channels & volume">
          <ul className="space-y-3">
            {[
              { ch: "SMS", value: 286, accent: "bg-primary" },
              { ch: "Email", value: 1840, accent: "bg-emerald-500" },
              { ch: "Push", value: 92, accent: "bg-amber-500" },
              { ch: "WhatsApp", value: 41, accent: "bg-rose-500" },
            ].map(row => (
              <li key={row.ch}>
                <div className="mb-1 flex items-center justify-between text-xs">
                  <span className="font-medium text-foreground">{row.ch}</span>
                  <span className="text-muted-foreground">
                    {row.value.toLocaleString("en-IN")}
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className={`h-full ${row.accent}`}
                    style={{
                      width: `${Math.min(100, (row.value / 1840) * 100)}%`,
                    }}
                  />
                </div>
              </li>
            ))}
          </ul>
        </SectionCard>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <SectionCard title="SMS Templates" subtitle="Most used">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Template</TableHead>
                  <TableHead>Usage</TableHead>
                  <TableHead>Last Used</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mockSmsTemplates.map(t => (
                  <TableRow key={t.id}>
                    <TableCell className="font-medium">{t.name}</TableCell>
                    <TableCell>{t.usage}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {t.lastUsed}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </SectionCard>

        <SectionCard title="Email Templates" subtitle="Most used">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Template</TableHead>
                  <TableHead>Usage</TableHead>
                  <TableHead>Last Used</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mockEmailTemplates.map(t => (
                  <TableRow key={t.id}>
                    <TableCell className="font-medium">{t.name}</TableCell>
                    <TableCell>{t.usage.toLocaleString("en-IN")}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {t.lastUsed}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </SectionCard>
      </div>
    </AdminShell>
  );
}
